import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { enqueueSnackbar } from 'notistack';

export const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4070/api';

type FeedbackMode = 'on' | 'off' | 'auto';

function API(options: { feedbackMode?: FeedbackMode } = {}): AxiosInstance {
  const feedbackMode = options.feedbackMode || 'auto';
  const lang = localStorage.getItem('lang')?.includes('"en"') ? 'en' : 'zh-CN';

  const api = axios.create({
    baseURL: API_URL,
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Accept-Language'] = lang;
    return config;
  });

  api.interceptors.response.use(
    (response: AxiosResponse) => {
      // Show success message for GET requests with feedbackMode 'on'
      // Or for any other request with feedbackMode 'on' or 'auto'
      const isGetRequest = response.config.method?.toLowerCase() === 'get';

      const shouldShowMessage = 
        (isGetRequest && feedbackMode === 'on') || 
        (!isGetRequest && (feedbackMode === 'on' || feedbackMode === 'auto'));
      
      if (shouldShowMessage && response.data?.message) {
        enqueueSnackbar(response.data.message, { variant: 'success', autoHideDuration: 2000 });
      }
      
      return response;
    },
    (error) => {
      const method = error.config?.method?.toLowerCase();
      const isGetRequest = method === 'get';

      // Determine whether we should show the error message
      const shouldShowError =
        (feedbackMode === 'on') ||
        (!isGetRequest && feedbackMode === 'auto');

      if (shouldShowError && error.response?.data?.message) {
        enqueueSnackbar(error.response.data.message, { variant: 'error', autoHideDuration: 2000 });
      }
      return Promise.reject(error);
    }
  );

  return api;
}

export default API;